import { useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../../../hooks/usePersistentState";
import BackButton from "../../../components/navigation/BackButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useCustomers } from "../../../hooks/useCustomers";
import { useAnimals } from "../../../hooks/useAnimals";
import { usePayments } from "../../../hooks/usePayments";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { useUsers } from "../../../hooks/useUsers";
import type { CustomerItem } from "../../../mocks/customers";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import CustomerSearch from "./components/CustomerSearch";
import CustomerRow, { CUSTOMER_ROW_GRID, CUSTOMER_ROW_MIN_WIDTH } from "./components/CustomerRow";
import SatisEkleDrawer from "./components/SatisEkleDrawer";
import type {
  SaleFormData,
  SalePresetSelection,
} from "./components/SatisEkleDrawer";
import { createGroupedSale } from "../../../lib/salesFlow";
import UserModal, {
  type UserFormData,
} from "../kullanici-yonetimi/components/UserModal";
import { getNextUserId } from "../../../store/usersStore";
import { sendSmsByTemplate } from "../../../lib/smsService";
import { getReportPrintSettings } from "../../../lib/reportPrintSettings";
import { buildSalePrintPayload, openCustomerSummaryPrintWindow, openSalePrintWindow } from "../../../lib/printTemplates";
import HeaderFilterCell, {
  FilterFooter,
  FilterOptionList,
  FilterRangeInputs,
  FilterSearchInput,
  FilterSortControls,
} from "../../../components/table/HeaderFilterCell";
import {
  matchesAnyOf,
  matchesNumericRange,
  compareTextForSort,
  compareNumberForSort,
  applySortDirection,
  type NumericRangeFilter,
  type SortDirection,
} from "../../../lib/tableFilters";
import { applyOperationResult } from "../../../lib/operationRefresh";
import { createSaleOperationRequest } from "../../../lib/api";

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

interface IncomingAnimalSaleState {
  fromAnimalSale?: boolean;
  animalId?: number;
  slotNo?: number;
  defaultKesimUcreti?: number;
  defaultParcalamaUcreti?: number;
  defaultTasimaUcreti?: number;
}

interface ExportCustomerRow {
  adSoyad: string;
  satisNotu: string;
  telefon: string;
  toplamHisse: number;
  toplamBorc: number;
  toplamOdenen: number;
  kalan: number;
  not: string;
}

interface CustomerHeaderFilters {
  status: string[];
  hisse: NumericRangeFilter;
  borc: NumericRangeFilter;
  odenen: NumericRangeFilter;
  kalan: NumericRangeFilter;
}

const EMPTY_HEADER_FILTERS: CustomerHeaderFilters = {
  status: [],
  hisse: { min: "", max: "" },
  borc: { min: "", max: "" },
  odenen: { min: "", max: "" },
  kalan: { min: "", max: "" },
};

type CustomerSortKey =
  | "adSoyad"
  | "satisNotu"
  | "telefon"
  | "durum"
  | "hisse"
  | "borc"
  | "odenen"
  | "kalan";

const CUSTOMER_SORT_LABELS: Record<CustomerSortKey, string> = {
  adSoyad: "Ad Soyad",
  satisNotu: "Satış Notu",
  telefon: "Telefon",
  durum: "Durum",
  hisse: "Hisse",
  borc: "Toplam Borç",
  odenen: "Ödenen",
  kalan: "Kalan",
};

function compareCustomersByKey(
  a: CustomerItem,
  b: CustomerItem,
  key: CustomerSortKey,
  salesNoteMap: Map<number, string[]>
) {
  if (key === "adSoyad") return compareTextForSort(a.adSoyad, b.adSoyad);
  if (key === "satisNotu") return compareTextForSort((salesNoteMap.get(a.id) ?? []).join(" • "), (salesNoteMap.get(b.id) ?? []).join(" • "));
  if (key === "telefon") return compareTextForSort(a.telefon, b.telefon);
  if (key === "durum") return compareTextForSort(customerStatus(a), customerStatus(b));
  if (key === "hisse") return compareNumberForSort(a.toplamHisseSayisi, b.toplamHisseSayisi);
  if (key === "borc") return compareNumberForSort(a.toplamBorc, b.toplamBorc);
  if (key === "odenen") return compareNumberForSort(a.toplamOdenen, b.toplamOdenen);
  if (key === "kalan") return compareNumberForSort(a.toplamBorc - a.toplamOdenen, b.toplamBorc - b.toplamOdenen);
  return 0;
}

function customerStatus(customer: CustomerItem) {
  const kalan = customer.toplamBorc - customer.toplamOdenen;
  if (customer.toplamHisseSayisi === 0) return "Kayıtlı";
  if (kalan === 0) return "Tam";
  if (customer.toplamOdenen > 0) return "Kısmi";
  return "Bekliyor";
}

function toggleSelection(list: string[], value: string) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function isHeaderFilterActive(filters: CustomerHeaderFilters) {
  return (
    filters.status.length > 0 ||
    Boolean(filters.hisse.min || filters.hisse.max) ||
    Boolean(filters.borc.min || filters.borc.max) ||
    Boolean(filters.odenen.min || filters.odenen.max) ||
    Boolean(filters.kalan.min || filters.kalan.max)
  );
}

export default function MusterilerPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { customers: customerData, setAllCustomers } = useCustomers();
  const { animals, setAllAnimals } = useAnimals();
  const { payments, setAllPayments } = usePayments();
  const { setAllUsers } = useUsers();
  const { setAllBankTransfers } = useBankTransfers();

  const [phoneSearch, setPhoneSearch] = usePersistentState("ec.filters.musteriler.phone.v1", "");
  const [nameSearch, setNameSearch] = usePersistentState("ec.filters.musteriler.name.v1", "");
  const [noteFilter, setNoteFilter] = usePersistentState<"Tümü" | "Not Var" | "Not Yok">("ec.filters.musteriler.note.v1", "Tümü");
  const [kalanOnly, setKalanOnly] = usePersistentState("ec.filters.musteriler.kalanOnly.v1", false);
  const [headerFilters, setHeaderFilters] = usePersistentState<CustomerHeaderFilters>("ec.filters.musteriler.header.v1", EMPTY_HEADER_FILTERS);
  const [sortKey, setSortKey] = usePersistentState<CustomerSortKey>("ec.sort.musteriler.key.v1", "adSoyad");
  const [sortDir, setSortDir] = usePersistentState<SortDirection>("ec.sort.musteriler.dir.v1", "asc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [satisDrawerCustomer, setSatisDrawerCustomer] = useState<CustomerItem | null>(
    null
  );
  const [pendingAnimalSale, setPendingAnimalSale] =
    useState<SalePresetSelection | null>(null);

  useEffect(() => {
    const state = (location.state as IncomingAnimalSaleState | null) ?? null;

    if (!state?.fromAnimalSale || !state.animalId || !state.slotNo) return;

    setPendingAnimalSale({
      animalId: state.animalId,
      slotNo: state.slotNo,
      defaultKesimUcreti: state.defaultKesimUcreti,
      defaultParcalamaUcreti: state.defaultParcalamaUcreti,
      defaultTasimaUcreti: state.defaultTasimaUcreti,
    });

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const pendingAnimal = useMemo(() => {
    if (!pendingAnimalSale?.animalId) return null;
    return animals.find((animal) => animal.id === pendingAnimalSale.animalId) ?? null;
  }, [animals, pendingAnimalSale]);

  const salesNoteMap = useMemo(() => {
    const map = new Map<number, string[]>();

    for (const animal of animals) {
      for (const share of animal.hisseler) {
        if (!share.musteriId) continue;
        const note = share.satisNotu?.trim();
        if (!note) continue;

        const list = map.get(share.musteriId) ?? [];
        if (!list.includes(note)) list.push(note);
        map.set(share.musteriId, list);
      }
    }

    return map;
  }, [animals]);

  const filtered = useMemo(() => {
    return customerData.filter((customer) => {
      const salesNotes = salesNoteMap.get(customer.id) ?? [];
      const kalan = customer.toplamBorc - customer.toplamOdenen;

      if (phoneSearch.trim()) {
        const q = phoneSearch.replace(/\s/g, "");
        if (!customer.telefon.replace(/\s/g, "").includes(q)) return false;
      }

      if (nameSearch.trim()) {
        if (
          !customer.adSoyad
            .toLocaleLowerCase("tr-TR")
            .includes(nameSearch.toLocaleLowerCase("tr-TR"))
        ) {
          return false;
        }
      }

      if (noteFilter === "Not Var" && salesNotes.length === 0) return false;
      if (noteFilter === "Not Yok" && salesNotes.length > 0) return false;
      if (kalanOnly && kalan <= 0) return false;
      if (!matchesAnyOf(customerStatus(customer), headerFilters.status)) return false;
      if (!matchesNumericRange(customer.toplamHisseSayisi, headerFilters.hisse)) return false;
      if (!matchesNumericRange(customer.toplamBorc, headerFilters.borc)) return false;
      if (!matchesNumericRange(customer.toplamOdenen, headerFilters.odenen)) return false;
      if (!matchesNumericRange(kalan, headerFilters.kalan)) return false;

      return true;
    }).sort((a, b) => applySortDirection(compareCustomersByKey(a, b, sortKey, salesNoteMap), sortDir));
  }, [customerData, headerFilters, kalanOnly, nameSearch, noteFilter, phoneSearch, salesNoteMap, sortDir, sortKey]);

  function renderSortControls(key: CustomerSortKey, ascLabel?: string, descLabel?: string) {
    return (
      <FilterSortControls
        activeDirection={sortKey === key ? sortDir : null}
        ascLabel={ascLabel}
        descLabel={descLabel}
        onSort={(direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
      />
    );
  }

  function exportCurrent() {
    const rows: ExportCustomerRow[] = filtered.map((customer) => ({
      adSoyad: customer.adSoyad,
      satisNotu: (salesNoteMap.get(customer.id) ?? []).join(" • "),
      telefon: customer.telefon,
      toplamHisse: customer.toplamHisseSayisi,
      toplamBorc: customer.toplamBorc,
      toplamOdenen: customer.toplamOdenen,
      kalan: customer.toplamBorc - customer.toplamOdenen,
      not: customer.kisaNot ?? "",
    }));

    const columns: ExportColumn<ExportCustomerRow>[] = [
      { key: "adSoyad", label: "Ad Soyad" },
      { key: "satisNotu", label: "Satış Notu" },
      { key: "telefon", label: "Telefon" },
      { key: "toplamHisse", label: "Toplam Hisse" },
      { key: "toplamBorc", label: "Toplam Borç" },
      { key: "toplamOdenen", label: "Toplam Ödenen" },
      { key: "kalan", label: "Kalan" },
      { key: "not", label: "Kısa Not" },
    ];

    exportRowsToCsv(
      `musteriler-${new Date().toISOString().slice(0, 10)}`,
      rows,
      columns
    );
  }

  async function handleAddCustomer(data: UserFormData) {
    const nextCustomerId =
      customerData.length > 0
        ? Math.max(...customerData.map((item) => item.id)) + 1
        : 1;
    const initialPassword = data.sifre?.trim() || "Abc1234";

    await setAllCustomers((prev) => [
      ...prev,
      {
        id: nextCustomerId,
        adSoyad: data.adSoyad,
        telefon: data.telefon,
        kisaNot: data.kisaNot || undefined,
        toplamHisseSayisi: 0,
        toplamBorc: 0,
        toplamOdenen: 0,
      },
    ]);

    await setAllUsers((prev) => [
      {
        id: getNextUserId(),
        adSoyad: data.adSoyad,
        telefon: data.telefon,
        email: data.email,
        rol: "Müşteri",
        durum: data.durum,
        eklemeTarihi: new Date().toISOString().slice(0, 10),
        sonGiris: undefined,
        sifre: initialPassword,
        customerId: nextCustomerId,
        forcePasswordChange: true,
        smsOperationalOptIn: data.smsOperationalOptIn,
        smsMarketingOptIn: data.smsMarketingOptIn,
      },
      ...prev,
    ]);

    void sendSmsByTemplate({
      templateKey: "welcomeTempPassword",
      channel: "auth",
      eventType: "welcome_temp_password",
      phone: data.telefon,
      customerId: nextCustomerId,
      variables: {
        customerName: data.adSoyad,
        password: initialPassword,
      },
    });
  }

  async function handleSatisKaydet(data: SaleFormData) {
    const selectedCustomer = customerData.find(
      (item) => item.id === data.customerId
    );
    if (!selectedCustomer) return;

    try {
      const result = await createSaleOperationRequest(data as unknown as Record<string, unknown>);
      await applyOperationResult(result);

      const printSettings = getReportPrintSettings();
      if (printSettings.saleAutoPrint) {
        const animal = (result.animals as any[]).find((item) => item.id === data.animalId);
        if (animal) {
          openSalePrintWindow(buildSalePrintPayload({
            animal,
            customerName: selectedCustomer.adSoyad,
            hisseLabel: data.hisseNo,
            saleAmount: data.birimSatisTutar,
            kesimUcreti: data.kesimUcreti,
            parcalamaUcreti: data.parcalamaUcreti,
            tasimaUcreti: data.tasimaUcreti,
            grandTotal: data.toplam,
            depositAmount: data.pesinatTutar,
            remainingAmount: data.toplam - data.pesinatTutar,
            operationalNote: data.kisaNot,
            fisNo: data.fisNo,
            tarih: data.tarih,
          }), printSettings);
        }
      }

      setPendingAnimalSale(null);
      setSatisDrawerCustomer(null);
    } catch (error) {
      console.error(error);
      window.alert("Satış kaydı sırasında çakışma veya bağlantı hatası oluştu. Lütfen son veriyi kontrol edip tekrar deneyin.");
    }
  }

  const totalBorc = filtered.reduce((sum, customer) => sum + customer.toplamBorc, 0);
  const totalOdenen = filtered.reduce(
    (sum, customer) => sum + customer.toplamOdenen,
    0
  );
  const totalKalan = totalBorc - totalOdenen;
  const kalanCount = filtered.filter(
    (customer) => customer.toplamBorc - customer.toplamOdenen > 0
  ).length;
  const hasAnyFilter =
    Boolean(phoneSearch || nameSearch) ||
    noteFilter !== "Tümü" ||
    kalanOnly ||
    isHeaderFilterActive(headerFilters);

  function clearAllFilters() {
    setPhoneSearch("");
    setNameSearch("");
    setNoteFilter("Tümü");
    setKalanOnly(false);
    setHeaderFilters(EMPTY_HEADER_FILTERS);
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-[1450px] mx-auto px-3 sm:px-5 py-4">
        <div className="lg:sticky lg:top-0 z-40 -mx-3 border-b border-farm-border/70 bg-farm-cream/95 px-3 pb-3 pt-1 shadow-sm backdrop-blur sm:-mx-5 sm:px-5">
          <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Müşteriler
            </h1>
            <p className="font-body text-xs text-farm-muted">
              Telefon, isim, satış notu ve başlık filtreleriyle müşteri süzün
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
          <BackButton />
            {hasAnyFilter ? (
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-muted text-xs font-semibold hover:bg-farm-cream"
              >
                <i className="ri-filter-off-line text-base" />
                Filtreleri Temizle
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
              className="xl:hidden inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:bg-farm-cream"
            >
              <i className="ri-filter-3-line text-base" />
              Mobil Filtre
            </button>
            <button
              type="button"
              onClick={exportCurrent}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
            >
              <i className="ri-file-excel-2-line text-base" />
              Excel Çıktısı
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {[
            { label: "Kayıtlı Müşteri", value: String(customerData.length), icon: "ri-group-2-line", color: "text-farm-brown" },
            { label: "Toplam Borç", value: fmt(totalBorc), icon: "ri-file-list-3-line", color: "text-farm-brown" },
            { label: "Toplam Ödenen", value: fmt(totalOdenen), icon: "ri-check-double-line", color: "text-farm-green" },
            { label: "Kalan Bakiyeli", value: `${kalanCount} müşteri`, icon: "ri-time-line", color: kalanCount > 0 ? "text-farm-earth" : "text-farm-muted" },
          ].map((item) => (
            <div key={item.label} className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5 flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-farm-cream ${item.color} flex-shrink-0`}>
                <i className={`${item.icon} text-sm`} />
              </div>
              <div className="min-w-0">
                <p className={`font-body font-bold text-sm leading-none truncate ${item.color}`}>{item.value}</p>
                <p className="font-body text-[10px] text-farm-muted mt-0.5 leading-none whitespace-nowrap">{item.label}</p>
              </div>
            </div>
          ))}
        </div>

        {pendingAnimalSale && (
          <div className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-md bg-farm-earth-pale/60 border border-farm-earth/20 mb-3">
            <div className="flex items-start gap-2 min-w-0">
              <div className="w-4 h-4 flex items-center justify-center text-farm-earth flex-shrink-0 mt-0.5">
                <i className="ri-route-line text-sm" />
              </div>
              <div className="min-w-0">
                <p className="font-body text-xs font-semibold text-farm-brown">Hayvan detayından satış akışı başlatıldı</p>
                <p className="font-body text-xs text-farm-muted mt-0.5">
                  Seçilen boş slot: <span className="font-semibold">{pendingAnimal?.kupeNo ?? `Hayvan #${pendingAnimalSale.animalId}`}</span> · <span className="font-semibold">Hisse {pendingAnimalSale.slotNo}</span>. Aşağıdan bir müşteri seçip <span className="font-semibold">Satış Ekle</span> ile devam edin.
                </p>
                {pendingAnimal && <p className="font-body text-[11px] text-farm-muted mt-1">Tür: {pendingAnimal.tur} · Toplam hisse: {pendingAnimal.toplamHisse}</p>}
              </div>
            </div>
            <button type="button" onClick={() => setPendingAnimalSale(null)} className="px-2.5 py-1 rounded-full border border-farm-border text-farm-muted text-[10px] font-semibold hover:bg-farm-cream transition-colors whitespace-nowrap">İptal</button>
          </div>
        )}

        {kalanOnly && totalKalan > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-farm-earth-pale/60 border border-farm-earth/20 mb-3">
            <div className="w-4 h-4 flex items-center justify-center text-farm-earth flex-shrink-0"><i className="ri-wallet-3-line text-sm" /></div>
            <p className="font-body text-xs text-farm-muted">Gösterilen müşterilerde toplam <span className="font-bold text-farm-earth">{fmt(totalKalan)}</span> kalan bakiye bulunuyor.</p>
          </div>
        )}

        <div className="mb-3">
          <CustomerSearch
            phoneSearch={phoneSearch}
            nameSearch={nameSearch}
            noteFilter={noteFilter}
            onPhoneChange={setPhoneSearch}
            onNameChange={setNameSearch}
            onNoteFilterChange={setNoteFilter}
            kalanOnly={kalanOnly}
            onKalanOnlyChange={setKalanOnly}
            onAddCustomer={() => setShowCustomerModal(true)}
            total={customerData.length}
            filtered={filtered.length}
          />
        </div>

        {mobileFiltersOpen && (
          <div className="xl:hidden rounded-farm border border-farm-border bg-white p-3 mb-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FilterSearchInput value={nameSearch} onChange={setNameSearch} placeholder="Ad Soyad ara" />
              <FilterSearchInput value={phoneSearch} onChange={setPhoneSearch} placeholder="Telefon ara" />
              <select value={noteFilter} onChange={(e) => setNoteFilter(e.target.value as "Tümü" | "Not Var" | "Not Yok")} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                <option value="Tümü">Satış notu: Tümü</option>
                <option value="Not Var">Not Var</option>
                <option value="Not Yok">Not Yok</option>
              </select>
              <select value={headerFilters.status[0] ?? ""} onChange={(e) => setHeaderFilters((prev) => ({ ...prev, status: e.target.value ? [e.target.value] : [] }))} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                <option value="">Durum: Tümü</option>
                <option value="Kayıtlı">Kayıtlı</option>
                <option value="Tam">Tam</option>
                <option value="Kısmi">Kısmi</option>
                <option value="Bekliyor">Bekliyor</option>
              </select>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value as CustomerSortKey)} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                {(Object.keys(CUSTOMER_SORT_LABELS) as CustomerSortKey[]).map((key) => (
                  <option key={key} value={key}>Sırala: {CUSTOMER_SORT_LABELS[key]}</option>
                ))}
              </select>
              <button type="button" onClick={() => setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))} className="h-10 rounded-xl border border-farm-border bg-white px-3 text-sm font-semibold text-farm-brown">
                {sortDir === "asc" ? "Artan" : "Azalan"}
              </button>
            </div>
            <button type="button" onClick={clearAllFilters} className="w-full h-10 rounded-xl border border-farm-border text-sm font-semibold text-farm-muted">Filtreleri Temizle</button>
          </div>
        )}

        <div className="bg-farm-warm border border-farm-border border-b-0 rounded-t-farm overflow-visible">
            <div className="overflow-visible">
              <div
                className="hidden bg-farm-cream xl:grid w-full border-b border-farm-border text-[10px] font-body font-semibold text-farm-muted uppercase tracking-wide shadow-sm"
              style={{ gridTemplateColumns: CUSTOMER_ROW_GRID, minWidth: `${CUSTOMER_ROW_MIN_WIDTH}px` }}
            >
              <HeaderFilterCell label="Ad Soyad / Not" active={Boolean(nameSearch) || sortKey === "adSoyad"}>
                {renderSortControls("adSoyad")}
                <FilterSearchInput value={nameSearch} onChange={setNameSearch} placeholder="Ad Soyad ara" />
                <FilterFooter onClear={() => setNameSearch("")} hint="İçerir araması" />
              </HeaderFilterCell>
              <HeaderFilterCell label="Satış Notu" active={noteFilter !== "Tümü" || sortKey === "satisNotu"}>
                {renderSortControls("satisNotu")}
                <FilterOptionList options={["Not Var", "Not Yok"]} selected={noteFilter === "Tümü" ? [] : [noteFilter]} onToggle={(value) => setNoteFilter(noteFilter === value ? "Tümü" : (value as "Not Var" | "Not Yok"))} />
                <FilterFooter onClear={() => setNoteFilter("Tümü")} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Telefon" active={Boolean(phoneSearch) || sortKey === "telefon"}>
                {renderSortControls("telefon")}
                <FilterSearchInput value={phoneSearch} onChange={setPhoneSearch} placeholder="Telefon ara" />
                <FilterFooter onClear={() => setPhoneSearch("")} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Durum" active={headerFilters.status.length > 0 || sortKey === "durum"}>
                {renderSortControls("durum")}
                <FilterOptionList options={["Kayıtlı", "Tam", "Kısmi", "Bekliyor"]} selected={headerFilters.status} onToggle={(value) => setHeaderFilters((prev) => ({ ...prev, status: toggleSelection(prev.status, value) }))} />
                <FilterFooter onClear={() => setHeaderFilters((prev) => ({ ...prev, status: [] }))} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Hisse" active={Boolean(headerFilters.hisse.min || headerFilters.hisse.max) || sortKey === "hisse"} align="right">
                {renderSortControls("hisse", "Küçükten büyüğe", "Büyükten küçüğe")}
                <FilterRangeInputs min={headerFilters.hisse.min} max={headerFilters.hisse.max} onMinChange={(value) => setHeaderFilters((prev) => ({ ...prev, hisse: { ...prev.hisse, min: value } }))} onMaxChange={(value) => setHeaderFilters((prev) => ({ ...prev, hisse: { ...prev.hisse, max: value } }))} />
                <FilterFooter onClear={() => setHeaderFilters((prev) => ({ ...prev, hisse: { min: "", max: "" } }))} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Toplam Borç" active={Boolean(headerFilters.borc.min || headerFilters.borc.max) || sortKey === "borc"} align="right">
                {renderSortControls("borc", "Küçükten büyüğe", "Büyükten küçüğe")}
                <FilterRangeInputs min={headerFilters.borc.min} max={headerFilters.borc.max} onMinChange={(value) => setHeaderFilters((prev) => ({ ...prev, borc: { ...prev.borc, min: value } }))} onMaxChange={(value) => setHeaderFilters((prev) => ({ ...prev, borc: { ...prev.borc, max: value } }))} />
                <FilterFooter onClear={() => setHeaderFilters((prev) => ({ ...prev, borc: { min: "", max: "" } }))} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Ödenen" active={Boolean(headerFilters.odenen.min || headerFilters.odenen.max) || sortKey === "odenen"} align="right">
                {renderSortControls("odenen", "Küçükten büyüğe", "Büyükten küçüğe")}
                <FilterRangeInputs min={headerFilters.odenen.min} max={headerFilters.odenen.max} onMinChange={(value) => setHeaderFilters((prev) => ({ ...prev, odenen: { ...prev.odenen, min: value } }))} onMaxChange={(value) => setHeaderFilters((prev) => ({ ...prev, odenen: { ...prev.odenen, max: value } }))} />
                <FilterFooter onClear={() => setHeaderFilters((prev) => ({ ...prev, odenen: { min: "", max: "" } }))} />
              </HeaderFilterCell>
              <HeaderFilterCell label="Kalan" active={Boolean(headerFilters.kalan.min || headerFilters.kalan.max) || sortKey === "kalan"} align="right">
                {renderSortControls("kalan", "Küçükten büyüğe", "Büyükten küçüğe")}
                <FilterRangeInputs min={headerFilters.kalan.min} max={headerFilters.kalan.max} onMinChange={(value) => setHeaderFilters((prev) => ({ ...prev, kalan: { ...prev.kalan, min: value } }))} onMaxChange={(value) => setHeaderFilters((prev) => ({ ...prev, kalan: { ...prev.kalan, max: value } }))} />
                <FilterFooter onClear={() => setHeaderFilters((prev) => ({ ...prev, kalan: { min: "", max: "" } }))} />
              </HeaderFilterCell>
                <div className="px-2 py-2 text-center">İşlem</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-farm-warm border border-farm-border border-t-0 rounded-b-farm overflow-visible">
          <div className="overflow-visible">
            {filtered.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto text-farm-muted mb-2"><i className="ri-search-line text-3xl" /></div>
                <p className="font-body text-sm text-farm-muted">Müşteri bulunamadı</p>
              </div>
            ) : (
              filtered.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  phoneHighlight={phoneSearch}
                  nameHighlight={nameSearch}
                  salesNoteSummary={(salesNoteMap.get(customer.id) ?? []).join(" • ")}
                  onSatisEkle={() => setSatisDrawerCustomer(customer)}
                  onPrint={(item) => openCustomerSummaryPrintWindow(item, animals, payments)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {showCustomerModal && (
        <UserModal
          mode="add"
          forceRole="Müşteri"
          titleOverride="Yeni Müşteri Ekle"
          subtitleOverride="Kullanıcı Yönetimi ile aynı müşteri kayıt formu"
          onClose={() => setShowCustomerModal(false)}
          onSave={handleAddCustomer}
        />
      )}

      {satisDrawerCustomer && (
        <SatisEkleDrawer
          customer={satisDrawerCustomer}
          presetSelection={pendingAnimalSale}
          onClose={() => setSatisDrawerCustomer(null)}
          onSave={handleSatisKaydet}
        />
      )}
    </div>
  );
}
